import { Injectable } from '@angular/core';
import { createRootState, RootState } from './store';
import cloneDeep from 'lodash.clonedeep';
import { PreparedDispatch, PreparedSelector, dispatch, select } from './core';
import { collectOldLocalstorageItems, migrateOldStorage } from './migration/migration';
import { deleteCurrentRegistration, loadRegisterSlice, selectCurrentRegistration } from './slices/register';
import { Timestamp } from '@shared/utils/timestamp';
import { deleteLogin, loadLoginSlice, selectLogin } from './slices/login';
import { loadAccountSlice } from './slices/account';
import { loadCredentialsSlice } from './slices/credentials';
import { loadDebugSlice } from './slices/debug';
import { loadSettingsSlice } from './slices/settings';
import { getStorage } from '@shared/dom';

const STORE_KEY = 'store';
const LOG_PREFIX = '[Store]';

@Injectable()
export class StoreService {
  private readonly storage = getStorage(localStorage) ?? getStorage(sessionStorage);

  private state: RootState;

  constructor() {
    this.loadState();
  }

  select<T extends unknown = unknown>(selector: PreparedSelector<T>): T {
    return select(this.getState(), selector);
  }

  dispatch(reducer: PreparedDispatch) {
    let state = this.getState();
    state = dispatch(state, reducer);
    this.save(state);
  }

  private getState() {
    return cloneDeep(this.state);
  }

  private loadState() {
    this.migrate();
    this.state = createRootState();
    const loaded = this.deserialize();
    this.dispatch(loadAccountSlice(loaded.account));
    this.dispatch(loadCredentialsSlice(loaded.credentials));
    this.dispatch(loadDebugSlice(loaded.debug));
    this.dispatch(loadLoginSlice(loaded.login));
    this.dispatch(loadRegisterSlice(loaded.register));
    this.dispatch(loadSettingsSlice(loaded.settings));
    this.removeExpiredData();
    this.serialize(this.state);
  }

  /**
   * Save a new state to the store.
   * @param state The new state
   */
  private save(state: RootState) {
    if (state.object !== 'RootState') {
      console.error(LOG_PREFIX, 'Attempted to save something other than a root state object.', state);
      throw Error('Attempted to save something other than a root state object.');
    }
    this.serialize(state);
    this.state = state;
  }

  /**
   * Serialize the state to local storage.
   * @param state The state to serialize
   */
  private serialize(state: RootState) {
    try {
      this.storage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error(LOG_PREFIX, "Couldn't serialize state:", state, e);
    }
  }

  /**
   * Deserialize state from storage.
   * @returns The dererialized state
   */
  private deserialize(): RootState {
    try {
      const key = this.storage.getItem(STORE_KEY);
      if (!key) {
        return createRootState();
      }
      return JSON.parse(key);
    } catch (e) {
      console.error(LOG_PREFIX, "Couldn't de-serialize state:", this.storage.getItem(STORE_KEY), e);
      return createRootState();
    }
  }

  /**
   * Migrate old versions of the application state. This will modify local storage.
   */
  private migrate() {
    if (this.storage.length === 0) {
      return;
    }
    if (this.storage.getItem('store')) {
      return;
    }

    const oldStorage = collectOldLocalstorageItems();
    if (oldStorage.size === 0) {
      return;
    }

    console.debug(LOG_PREFIX, 'Migrating with data', oldStorage);
    this.state = createRootState();
    migrateOldStorage(this, oldStorage);

    for (const key of oldStorage.keys()) {
      localStorage.removeItem(key);
    }
  }

  /**
   * If anything is expired, remove it.
   */
  private removeExpiredData() {
    const now = Timestamp.now();

    // Delete expired registration.
    const reg = this.select(selectCurrentRegistration());
    if (reg && now >= reg.expiresAt) {
      this.dispatch(deleteCurrentRegistration());
    }

    // Delete expired login.
    const login = this.select(selectLogin());
    if (login && now >= login.expiresAt) {
      this.dispatch(deleteLogin());
    }
  }
}

export function provideStore(store = new StoreService()) {
  return { provide: StoreService, useValue: store };
}
